/**
 * This file is the interface for the games and launcher.  All calls to those elements should go through this API so that
 * when they are updated, we can localize those changes.  This file has a side-effect of initializing some objects on the
 * window's properties and lowercasing the existing property names if they exist.
 */
import IOSNativePurchaseIntegration from './IOSNativePurchaseIntegration';
import {
    initWebIntegration,
    configureWindowForWeb,
    getParameterByName,
    initDirectPurchaseIntegration,
    configureWindowForWebV2,
} from './WebPurchaseIntegration';
import {lowercaseObjectProperties} from '../src/utils/lowercaseObjectProperties';
import root from 'window-or-global';

export const launcherUserAgent = /.*(EpicGamesLauncher|UnrealEngine).*/;

if (!root.__server_side_render) {
    // This function will update the elements on the window
    configureWindowForWeb();
}

const log = function (logString, data) {
    console.log(`LauncherIntegration'  ${logString}`);
    if (data) {
        console.log(`        - data: ${JSON.stringify(data)}`);
    }
};

const isEngineCompatible = function (engineVersion, appVersions) {
    let compatibility = 1;
    Object.keys(appVersions).forEach((key) => {
        const appVersion = appVersions[key];
        const expectedAppVersion = appVersion.startsWith('UE_')
            ? appVersion.substring(3)
            : appVersion;
        //If the engine version is installed & matches the app version
        if (
            engineVersion.binstalled &&
            engineVersion.version &&
            engineVersion.version.startsWith(expectedAppVersion)
        ) {
            compatibility = engineVersion.bdefault ? 3 : 2; //check for if it is compatible or not.
        }
    });
    return compatibility;
};

const compatiblityValues = ['no-engine', 'incompatible', 'partial', 'compatible'];
let webMarketplace = null;
let vault = null;
let launcher = null;
let engines = null;
let purchaseFlow = null;
let integrationCheckLastRun = null;
let page = null;
let entitlements = null;
let isPurchasingInProgress = false;

const mockLauncher = function mockLauncher(url) {
    return !!getParameterByName('mockLauncher', url);
};

class LauncherIntegrationUtils {
    initIntegrationCheck() {
        integrationCheckLastRun = new Date().getTime();

        // if purchase flow is running in the iOS native client, patch bridges
        if (IOSNativePurchaseIntegration.isIOSNativeClient()) {
            IOSNativePurchaseIntegration.patch();
        }

        webMarketplace = root.ue ? root.ue.purchasing : null;
        if (webMarketplace) {
            log('Window object found for marketplace. (ue.purchasing)');
        } else {
            // Backwards compatiblity with old launcher versions
            webMarketplace = root.ue ? root.ue.webmarketplace : null;
            if (webMarketplace) {
                log('Window object found for marketplace. (ue.webmarketplace)');
            }
        }

        vault = root.ue ? root.ue.vault : null;
        if (vault) {
            log('Window object found for vault. (ue.vault)');
        }

        launcher = root.ue ? root.ue.launcher : null;
        if (launcher) {
            log('Window object found for launcher. (ue.launcher)');
        }

        engines = root.ue ? root.ue.engines : null;
        if (engines) {
            log('Window object found for engines. (ue.engines)');
        }

        purchaseFlow = root.ue ? root.ue.purchaseflow : null;
        if (purchaseFlow) {
            log('Window object found for Purchase Flow. (ue.purchaseflow)');
        }

        page = root.ue ? root.ue.page : null;
        if (page) {
            log('Window object found for Page. (ue.page)');
        }

        entitlements = root.ue ? root.ue.entitlements : null;
        if (entitlements) {
            log('Window object found for Entitlements. (ue.entitlements)');
        }
    }

    /**
     * Expose the init web integration function from the web integration utils.
     * @param initConfig the config to use
     * @param initOffer the offer to initalize
     * @param onClose close callback
     */
    initWebIntegration(initConfig, initOffer, onClose, consistencyCheckParams, onFailure) {
        initWebIntegration(initConfig, initOffer, onClose, consistencyCheckParams, onFailure);
    }

    /**
     * Expose the init web integration function from the web integration utils.
     * @param {Object} initConfig the config to use
     * @param {String} initConfig.paymentWebBase - paymentWebBase env
     * @param {*} initConfig.purchaseContainer - purchaseContainer element
     * @param {*} initConfig.purchaseBg - purchaseBg element
     * @param {Function} onClose -close callback
     * @param {Function} onFailure -failure callback
     */
    initDirectPurchaseIntegration(initConfig, onClose, onFailure) {
        initDirectPurchaseIntegration(initConfig, onClose, onFailure);
    }

    processLauncherObject(obj) {
        lowercaseObjectProperties(obj);
    }

    initPurchaseV2() {
        log('! Switching to v2 Purchase Flow !');
        // override bridge api
        configureWindowForWebV2();
        // update integration check
        this.initIntegrationCheck();
    }

    /**
     * It is possible that these functions are used before the initIntegrationCheck is called and the references
     * will all be null.  This ensures the variable references are checked before using them.
     */
    prep() {
        if (typeof integrationCheckLastRun === 'undefined' || !integrationCheckLastRun) {
            this.initIntegrationCheck();
        }
    }

    /**
     * Init the launcher integration module.
     */
    initSignInCapture() {
        this.prep();
        if (launcher) {
            const oldElement = root.document.getElementById('sign-in-link');
            if (!oldElement) return;
            const newElement = oldElement.cloneNode(true);
            oldElement.parentNode.replaceChild(newElement, oldElement);
            newElement.addEventListener('click', (event) => {
                event.preventDefault();
                this.requestSignIn();
            });
        }
    }

    requestPurchase(
        namespace,
        offerId,
        quantity,
        success,
        failure,
        fallback,
        highlightColor,
        consistencyCheckParams,
        locale
    ) {
        this.prep();
        log(`Request to purchase: ${namespace} - ${offerId} x ${quantity}`);
        if (webMarketplace) {
            isPurchasingInProgress = true;
            var offerIds = [].concat(offerId);
            var requestParams = [];
            for (let i = 0; i < offerIds.length; i++) {
                const requestParam = {
                    OfferNamespace: namespace,
                    OfferId: offerIds[i],
                    Quantity: quantity,
                    HighlightColor: highlightColor,
                    locale,
                };
                if (consistencyCheckParams) {
                    consistencyCheckParams.forEach((item) => {
                        if (item.offerId === offerIds[i]) {
                            requestParam.priceToCheck = item.discountPrice
                                ? item.discountPrice
                                : item.price;
                        }
                    });
                }
                requestParams.push(requestParam);
            }
            webMarketplace
                .requestpurchase(requestParams)
                .then((receipt) => {
                    log('Purchase Completed Successfully', receipt);
                    lowercaseObjectProperties(receipt);
                    if (success) success(receipt);
                })
                .catch((reason) => {
                    log('Purchase Failed (launcher)', reason);
                    if (failure) failure(false);
                });
        } else {
            log('Deferred to fallback.');
            if (typeof fallback === 'function') {
                fallback();
            }
        }
    }

    /**
     * Starts the purchase flow using the V2 methods
     * @param {object} params
     * @param {array} params.lineOffers
     * @param {string} params.lineOffers[].offerNamespace
     * @param {string} params.lineOffers[].offerId
     * @param {number} params.lineOffers[].quantity
     * @param {function} [params.fallback]
     * @param {string} [params.highlightColor]
     * @param {boolean} [params.showNavigation]
     * @returns Promise<Receipt>
     */
    requestPurchaseV2(params) {
        this.prep();
        return new Promise((resolve, reject) => {
            log('Request to purchase:');

            if (!params.lineOffers || !params.lineOffers.length) {
                log('--> (error) lineOffers are required for purchase');
                return reject('missing lineOffers');
            }

            if (!webMarketplace) {
                // fail early
                if (typeof params.fallback === 'function') {
                    log('--> (error) webMarketplace not found, deferred to fallback');
                    return params.fallback();
                } else {
                    log('--> (error) webMarketplace not found');
                    return reject('webMarketplace not found');
                }
            }

            // log invoice
            params.lineOffers.forEach((lineOffer) => {
                log(`--> ${lineOffer.offerNamespace} - ${lineOffer.offerId} x ${lineOffer.quantity}`);
            });

            isPurchasingInProgress = true;

            const onSuccess = (receipt) => {
                log('--> (success) Purchase Completed Successfully', receipt);
                lowercaseObjectProperties(receipt);
                resolve(receipt);
            };

            const onFailure = (reason) => {
                log('--> (error) Purchase Failed', reason);
                reject(reason);
            };

            /**
             * NOTE: this function is from window.webMarketplace (root.ue.purchasing), and can be either
             * requestpurchase or requestpurchasecart.
             *
             * If it's requestpurchase, then it's either WebPurchaseIntegration.RequestPurchaseV2
             * or what was injected by the launcher.
             *
             * If it's requestpurchasecart, it's a function injected from the launcher.
             */
            if (webMarketplace.requestpurchasecart) {
                return webMarketplace.requestpurchasecart(params).then(onSuccess).catch(onFailure);
            }

            // convert arguments to v1 format
            const requestParams = params.lineOffers.map((lineOffer) => {
                return {
                    OfferNamespace: lineOffer.offerNamespace,
                    OfferId: lineOffer.offerId,
                    Quantity: lineOffer.quantity,
                    HighlightColor: params.highlightColor,
                    showNavigation: params.showNavigation,
                };
                // NOTE: consistencyCheckParams has been removed because EG Store does not currently use it
            });

            return webMarketplace.requestpurchase(requestParams).then(onSuccess).catch(onFailure);
        });
    }

    requestDirectPurchase(params = {}, success, failure, fallback) {
        this.prep();
        if (purchaseFlow) {
            if (!params.purchaseToken) {
                console.warn('purchaseToken is required when requestPayment()');
                return;
            }
            isPurchasingInProgress = true;
            purchaseFlow
                .requestdirectpurchase(params)
                .then((receipt) => {
                    log('Purchase Completed Successfully', receipt);
                    lowercaseObjectProperties(receipt);
                    if (success) success(receipt);
                })
                .catch((reason) => {
                    log('Purchase Failed (launcher)', reason);
                    if (failure) failure(reason);
                });
        } else {
            log('Deferred to fallback.');
            if (typeof fallback === 'function') {
                fallback();
            }
        }
    }

    configureItemFromVault(itemId, callback) {
        this.prep();
        log(`Request to get item from vault: ${itemId}`);
        if (vault) {
            vault
                .getitem(itemId)
                .then((item) => {
                    log('Item returned from vault');
                    lowercaseObjectProperties(item);
                    if (callback) callback(item);
                })
                .catch((reason) => {
                    log('Item retrieval from vault failed');
                    if (callback) callback(reason);
                });
        } else {
            log('No vault item found, returning.');
            if (callback) callback(false);
        }
    }

    isUseVault() {
        this.prep();
        return !!vault;
    }

    requestSignIn(fallback) {
        this.prep();
        log('Sign In requested');
        if (launcher) {
            log('Delegating to launcher to sign in user.');
            launcher.requestsignin();
        } else if (fallback) {
            log('Deferring to fallback sign-in');
            fallback();
        }
    }

    /**
     * Check the compatibility of this item with the installed launcher versions
     * @param versions
     */
    checkItemCompatibility(appVersions, callback) {
        this.prep();
        log('Checking engine compatibility');
        if (!engines) {
            return null;
        }
        if (!appVersions) {
            return callback ? callback(compatiblityValues[0]) : null;
        }
        engines.getengines().then((engineVersions) => {
            lowercaseObjectProperties(engineVersions);
            this.processEngineCompatibility(engineVersions, appVersions, callback);
        });
    }

    /**
     * onEnginesChanged
     */
    onEntitlementsChanged(callback) {
        this.prep();
        if (!callback || typeof callback !== 'function' || !entitlements) return;
        entitlements
            .subscribe('OnChanged', (changeEvent) => {
                log('Entitlements changed, invoking callback');
                lowercaseObjectProperties(changeEvent);
                callback(changeEvent);
            })
            .then((pid) => {
                log(`Subscribed to Entitlement change (${pid})`);
            })
            .catch((error) => {
                log(`Failed to subscribe to Entitlement change ${error}`);
            });
    }

    /**
     * Process engine comaptibility rules
     */
    processEngineCompatibility(engineVersions, appVersions, callback) {
        let compatibility = 1;
        if (engineVersions) {
            Object.keys(engineVersions).forEach((key) => {
                const engineVersion = engineVersions[key];
                const engineCompatibility = isEngineCompatible(engineVersion, appVersions);
                compatibility =
                    engineCompatibility > compatibility ? engineCompatibility : compatibility;
            });
        }
        return callback ? callback(compatiblityValues[compatibility]) : null;
    }

    /**
     * onEnginesChanged
     */
    onEnginesChanged(appVersions, callback) {
        this.prep();
        if (!callback || typeof callback !== 'function' || !engines) return;
        engines
            .subscribe('EnginesChanged', (engineVersions) => {
                log('Engines changed, invoking callback');
                lowercaseObjectProperties(engineVersions);
                this.processEngineCompatibility(engineVersions, appVersions, callback);
            })
            .then((pid) => {
                log(`Subscribed to Engine change (${pid})`);
            })
            .catch((error) => {
                log(`Failed to subscribe to Engine change ${error}`);
            });
        engines.subscribe('SelectedEngineChanged', (engine) => {
            this.checkItemCompatibility(appVersions, callback);
        });
    }

    /**
     * Request that the launcher refresh their entitlements.
     */
    refreshEntitlements() {
        this.prep();
        log('Refreshing Entitlements');
        if (!vault) {
            return;
        }
        return vault.refreshentitlements();
    }

    /**
     * Listen for vault item changes
     */
    onVaultItemChange(callback) {
        this.prep();
        if (!callback || typeof callback !== 'function' || !vault) return;
        vault
            .subscribe('ItemsChanged', (data) => {
                log('Vault items changed, invoking callback');
                lowercaseObjectProperties(data);
                callback(data);
            })
            .then((pid) => {
                log(`Subscribed to vault item change (${pid})`);
            })
            .catch((error) => {
                log('Failed to subscribe to vault item change');
            });
    }

    /**
     * Display a page as a full-screen overlay in the launcher.
     * @param url
     */
    showOverlay(url) {
        this.prep();
        log('Opening Full Screen Overlay');
        if (launcher && url) {
            launcher.showoverlay('ue', url);
        }
    }

    /**
     * Close the full screen overlay that is displayed.
     */
    closeOverlay() {
        this.prep();
        log('Closing the full screen overlay');
        if (launcher) {
            launcher.closeoverlay();
        }
    }

    isOverlaySupported() {
        this.prep();
        return launcher;
    }

    toggleFullScreen() {
        this.prep();
        log('Requeted toggle fullscreen');
        if (!launcher) return true;
        launcher
            .isfullscreen()
            .then((fullScreen) => {
                if (fullScreen) {
                    launcher.exitfullscreen();
                } else {
                    launcher.requestfullscreen();
                }
            })
            .catch(() => {
                // ignore
            });
        return false;
    }

    requestCloseCancel() {
        this.prep();
        if (purchaseFlow) {
            purchaseFlow.requestclose('UserCanceled');
        }
    }

    requestCloseSuccess() {
        this.prep();
        if (purchaseFlow) {
            purchaseFlow.requestclose('WasSuccessful');
        }
    }

    receipt(receiptObject) {
        this.prep();
        log('LauncherIntegration - Begin submit of purchase receipt.');
        const offerList = [];
        if (!receiptObject || !receiptObject.receipt) {
            log('LauncherIntegration receipt failed to submit, no receipt object received.');
            return;
        }
        receiptObject.receipt.lineOffers.forEach((offer) => {
            const offerItems = [];
            if (offer.offerItems) {
                offer.offerItems.forEach((item) => {
                    offerItems.push({
                        ItemId: item.itemId,
                        ItemNamespace: item.namespace,
                        EntitlementId: item.entitlementId,
                        EntitlementName: item.entitlementName,
                    });
                });
            }
            offerList.push({
                OfferNamespace: offer.namespace,
                OfferId: offer.offerId,
                Items: offerItems,
            });
        });
        const grantedVoucher = receiptObject.grantedVoucher;
        const receipt = {
            TransactionId: receiptObject.confirmation,
            TransactionState: 'Purchased',
            Offers: offerList,
            grantedVoucher,
        };
        log(`LauncherIntegration submitting receipt to client\n${JSON.stringify(receipt)}`);
        if (purchaseFlow) {
            purchaseFlow.receipt(receipt).then(() => {
                log('LauncherIntegration receipt submitted successfully');
            });
        }
    }

    /**
     * Functionality in the launcher to suspend entitlement checking indefinitely for this property.  This can be
     * used to allow pages to handle updates & confirms for purchases without the launcher taking over.
     * @returns {*}
     */
    pauseEntitlementRefresh() {
        this.prep();
        console.log('Requested pause of entitlement refreshing.');
        if (!page) return;
        return page.pauserefresh();
    }

    /**
     * Functionality in the launcher to resume entitlement refreshing and kick a refresh off.  This tells the launcher
     * that the critical section is complete and they can take over if required on entitlement fetch.
     * @returns {*}
     */
    resumeEntitlementRefresh() {
        this.prep();
        console.log('Requested resume of entitlement refreshing.');
        if (!page) return;
        return page.resumerefresh();
    }

    /**
     * Functionality in the launcher to request entitlement refresh (manually override if we are paused).
     * @returns {*}
     */
    requestEntitlementRefresh() {
        this.prep();
        console.log('Requested manual entitlement refresh.');
        if (!page) return;
        return page.requestrefresh();
    }

    isWebPurchaseFlow() {
        this.prep();
        return purchaseFlow && purchaseFlow.iswebflow;
    }

    isLauncherClient({url, userAgent}) {
        this.prep();
        return (
            mockLauncher(url) ||
            (launcher && typeof launcher === 'object') ||
            launcherUserAgent.test(userAgent || root.navigator.userAgent)
        );
    }

    setPurchasingInProgress(status) {
        isPurchasingInProgress = !!status;
    }

    isPurchasingInProgress() {
        return isPurchasingInProgress;
    }
}

const launcherIntegrationUtils = new LauncherIntegrationUtils();
export default launcherIntegrationUtils;
